import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Table from "examples/Tables/Table";
import ParentsProfileTableData from "./data/parentProfileTableData";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Card,
  Grid,
  Chip,
  Autocomplete,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ArgonButton from "components/ArgonButton";
import PropTypes from "prop-types";
import EventNoteIcon from "@mui/icons-material/EventNote";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import Dropdown from "components/CustomDropdown/Dropdown";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import ActivityAttendanceTableData2 from "Views/people/parents/parentProfile/data/activityAttendanceTableData2";
import ActivityAttendanceTableData3 from "Views/people/parents/parentProfile/data/activityAttemdanceTableData3";
import ActivityAttendanceTableData4 from "Views/people/parents/parentProfile/data/activityAttendanceTableData4";
import ArgonInput from "components/ArgonInput";
import axios from "utils/api";
import { baseURL } from "utils/api";
import { toast } from "react-toastify";
import logoEdushala from "assets/images/edushala.png";

// Icons for PDF
import PersonIcon from "@mui/icons-material/Person";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EscaolatorWarningIcon from "@mui/icons-material/EscalatorWarning";
import ClassIcon from "@mui/icons-material/Class";
import SchoolIcon from "@mui/icons-material/School";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import HistoryIcon from "@mui/icons-material/History";
import AssessmentIcon from "@mui/icons-material/Assessment";

function activityAttendance({ parentId, onStatusChange }) {
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [classSessions, setClassSessions] = useState([]);
  const [parentNotes, setParentNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [reportScope, setReportScope] = useState({ type: "ALL", studentId: null });
  const reportRef = useRef(null);

  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteInput, setNoteInput] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [showAllNotesOpen, setShowAllNotesOpen] = useState(false);

  // Student Edit States
  const [studentEditOpen, setStudentEditOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentName, setStudentName] = useState("");
  const [studentGender, setStudentGender] = useState("");
  const [studentDob, setStudentDob] = useState("");
  const [studentSchoolName, setStudentSchoolName] = useState("");
  const [studentPreferredTime, setStudentPreferredTime] = useState("");
  const [studentClassesPerWeek, setStudentClassesPerWeek] = useState("");
  const [studentModeOfTuition, setStudentModeOfTuition] = useState("");
  const [studentClass, setStudentClass] = useState("");
  const [studentBoard, setStudentBoard] = useState("");
  const [studentSubjects, setStudentSubjects] = useState([]);
  const [updatingStudent, setUpdatingStudent] = useState(false);

  // Dropdown data for class, board, subjects
  const [allClasses, setAllClasses] = useState([]);
  const [allBoards, setAllBoards] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);

  // Student delete states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);

  // Helper to safely render values
  const displayValue = (val, fallback = "NA") => {
    if (val === null || val === undefined || val === "") return fallback;
    if (typeof val === "object") {
      if (Array.isArray(val)) {
        return val
          .map((v) =>
            v && typeof v === "object" ? v.name || v.title || v._id || JSON.stringify(v) : v
          )
          .join(", ");
      }
      return val.name || val.title || val.boardName || val._id || fallback;
    }
    return val;
  };

  // Fetch classes for dropdown
  useEffect(() => {
    axios
      .get(`/classes?limit=100`)
      .then((res) => {
        if (res.data.success) {
          const activeClasses = (res.data.data || []).filter((cls) => !cls.isDeleted);
          const options = activeClasses.map((cls) => ({ label: cls.name, value: cls._id, ...cls }));
          setAllClasses(options);
        }
      })
      .catch((err) => console.error("Error fetching classes:", err));
  }, []);

  // Fetch boards for dropdown
  useEffect(() => {
    axios
      .get(`/boards?limit=100`)
      .then((res) => {
        if (res.data.success) {
          const activeBoards = (res.data.data || []).filter((board) => !board.isDeleted);
          const options = activeBoards.map((board) => ({
            label: board.name,
            value: board._id,
            ...board,
          }));
          setAllBoards(options);
        }
      })
      .catch((err) => console.error("Error fetching boards:", err));
  }, []);

  // Fetch subjects for dropdown
  useEffect(() => {
    axios
      .get(`/subjects?limit=500`)
      .then((res) => {
        if (res.data.success) {
          const options = res.data.data.map((subj) => ({
            label: subj.name,
            value: subj._id,
            ...subj,
          }));
          setAllSubjects(options);
        }
      })
      .catch((err) => console.error("Error fetching subjects:", err));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!parentId) {
        setLoading(false);
        return;
      }

      let hasError = false;
      try {
        setLoading(true);
        // Fetch students for the parent
        const studentsResponse = await axios.get(`/student?parentId=${parentId}`);
        let studentsData = [];
        if (studentsResponse.data.success) {
          studentsData = studentsResponse.data.data || [];
          setStudents(studentsData);
        }

        // Fetch attendance for each student
        if (studentsData.length > 0) {
          const attendancePromises = studentsData.map(async (student) => {
            try {
              const response = await axios.get(`/attendance?studentId=${student._id}`);
              if (!response.data.success) return [];

              return (response.data.data || []).map((item) => ({
                ...item,
                __studentId: student._id,
                __studentName: student?.name || student?.fullName || "NA",
                __studentClassId:
                  (typeof student?.class === "object" ? student?.class?._id : student?.class) ||
                  null,
                __studentClassName:
                  (typeof student?.class === "object"
                    ? student?.class?.name || student?.class?.className
                    : "") || "NA",
              }));
            } catch (err) {
              console.error(`Error fetching attendance for student ${student._id}:`, err);
              return [];
            }
          });
          const attendanceResponses = await Promise.all(attendancePromises);
          const allAttendance = attendanceResponses.flat();
          setAttendanceData(allAttendance);
        } else {
          setAttendanceData([]);
        }

        // Fetch class sessions for leave data
        try {
          const classRes = await axios.get(
            `/classes/parent/${parentId}/accept-tutor?status=ACTIVE&limit=100`
          );
          if (classRes.data?.success) {
            setClassSessions(classRes.data.data || []);
          } else {
            setClassSessions([]);
          }
        } catch (classError) {
          console.error("Error fetching class sessions:", classError);
          setClassSessions([]);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Error fetching data");
        hasError = true;
      } finally {
        setLoading(false);
        if (onStatusChange) {
          onStatusChange(hasError ? "error" : "success");
        }
      }
    };

    fetchData();
  }, [parentId, onStatusChange]);

  useEffect(() => {
    const fetchParentNotes = async () => {
      if (!parentId) return;
      try {
        setNotesLoading(true);
        const res = await axios.get(`/parent/${parentId}/notes`);
        if (res.data?.success) {
          setParentNotes(Array.isArray(res.data.data) ? res.data.data : []);
        } else {
          setParentNotes([]);
        }
      } catch (err) {
        console.error("Error fetching parent notes:", err);
        setParentNotes([]);
      } finally {
        setNotesLoading(false);
      }
    };

    fetchParentNotes();
  }, [parentId]);

  // Handle student edit open
  const handleStudentEditOpen = (student) => {
    setSelectedStudent(student);
    setStudentName(student.name || "");
    setStudentGender(student.gender || "");
    setStudentDob(student.dob ? new Date(student.dob).toISOString().split("T")[0] : "");
    setStudentSchoolName(student.schoolName || "");
    setStudentPreferredTime(student.preferredTimeDuration || "");
    setStudentClassesPerWeek(student.noOfClassesPerWeek || "");
    setStudentModeOfTuition(student.modeOfTuition || "");

    // Set class, board, and subjects
    setStudentClass(student.class?._id || student.class || "");
    setStudentBoard(student.board?._id || student.board || "");

    // Handle subjects array - could be objects or IDs
    const subjectIds = student.subjects ? student.subjects.map((s) => s._id || s) : [];
    setStudentSubjects(subjectIds);

    setStudentEditOpen(true);
  };

  const handleStudentEditClose = () => {
    setStudentEditOpen(false);
    setSelectedStudent(null);
    // Reset form fields
    setStudentName("");
    setStudentGender("");
    setStudentDob("");
    setStudentSchoolName("");
    setStudentPreferredTime("");
    setStudentClassesPerWeek("");
    setStudentModeOfTuition("");
    setStudentClass("");
    setStudentBoard("");
    setStudentSubjects([]);
  };

  // Delete handlers
  const handleStudentDeleteOpen = (student) => {
    console.log("handleStudentDeleteOpen called", student?._id);
    setStudentToDelete(student);
    setTimeout(() => setDeleteDialogOpen(true), 0);
  };

  const handleStudentDeleteClose = () => {
    setStudentToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleStudentDeleteConfirm = async () => {
    if (!studentToDelete?._id) return;
    try {
      const res = await axios.delete(`/student/${studentToDelete._id}`);
      if (res.data.success) {
        toast.success(res.data.message || "Student deleted successfully");
        // Refresh students
        const refreshRes = await axios.get(`/student?parentId=${parentId}`);
        if (refreshRes.data.success) {
          setStudents(refreshRes.data.data || []);
        }
      } else {
        toast.error(res.data.msg || "Failed to delete student");
      }
    } catch (err) {
      console.error("Error deleting student:", err);
      toast.error(err.response?.data?.msg || "Failed to delete student");
    } finally {
      handleStudentDeleteClose();
    }
  };

  const handleStudentReportDownload = (student) => {
    if (!student?._id) {
      toast.error("Student not found for download");
      return;
    }

    handleDownloadReport({ type: "STUDENT", studentId: student._id });
  };

  // Handle student update
  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    setUpdatingStudent(true);

    try {
      const updateData = {
        name: studentName,
        gender: studentGender,
        dob: studentDob,
        schoolName: studentSchoolName,
        preferredTimeDuration: studentPreferredTime,
        noOfClassesPerWeek: studentClassesPerWeek,
        modeOfTuition: studentModeOfTuition,
        class: studentClass || undefined,
        board: studentBoard || undefined,
        subjects: studentSubjects && studentSubjects.length > 0 ? studentSubjects : undefined,
      };

      const response = await axios.put(`/student/${selectedStudent._id}`, updateData);

      if (response.data.success) {
        toast.success("Student details updated successfully");
        handleStudentEditClose();

        // Refresh students list
        const refreshRes = await axios.get(`/student?parentId=${parentId}`);
        if (refreshRes.data.success) {
          setStudents(refreshRes.data.data || []);
        }
      } else {
        toast.error(response.data.msg || "Failed to update student details");
      }
    } catch (error) {
      console.error("Error updating student:", error);
      toast.error(error.response?.data?.msg || "Failed to update student details");
    } finally {
      setUpdatingStudent(false);
    }
  };

  const { columns, rows } = ParentsProfileTableData(
    students,
    handleStudentEditOpen,
    handleStudentDeleteOpen,
    handleStudentReportDownload
  );
  const { columns: prCols, rows: prRows } = ActivityAttendanceTableData2(attendanceData);
  const { columns: prCols2, rows: prRows2 } = ActivityAttendanceTableData3(parentNotes);
  const leaveEntries = useMemo(() => {
    const sessions = Array.isArray(classSessions) ? classSessions : [];
    const entries = [];
    const toDateKey = (date) => new Date(date).toISOString().split("T")[0];

    sessions.forEach((session) => {
      const leaveDates = Array.isArray(session?.leaveDate) ? session.leaveDate : [];
      const className = session?.className || session?.name || "NA";

      const filteredDates = leaveDates
        .map((leave) => {
          const leaveFor = typeof leave === "object" ? leave?.leaveFor : undefined;
          if (leaveFor && leaveFor !== "PARENT") return null;
          const dateValue = typeof leave === "object" ? leave?.date : leave;
          if (!dateValue) return null;
          const dateObj = new Date(dateValue);
          if (Number.isNaN(dateObj.getTime())) return null;
          return dateObj;
        })
        .filter(Boolean)
        .map((dateObj) => ({
          dateObj,
          key: toDateKey(dateObj),
        }))
        .sort((a, b) => (a.key < b.key ? -1 : 1));

      if (filteredDates.length === 0) return;

      let rangeStart = filteredDates[0].dateObj;
      let rangeEnd = filteredDates[0].dateObj;

      for (let i = 1; i < filteredDates.length; i += 1) {
        const prevKey = toDateKey(rangeEnd);
        const currentKey = filteredDates[i].key;
        const prevDate = new Date(`${prevKey}T00:00:00Z`);
        const currentDate = new Date(`${currentKey}T00:00:00Z`);
        const diffDays = (currentDate - prevDate) / (24 * 60 * 60 * 1000);

        if (diffDays === 1) {
          rangeEnd = filteredDates[i].dateObj;
        } else {
          entries.push({
            startDate: rangeStart,
            endDate: rangeEnd,
            addedOn: rangeStart,
            message: className !== "NA" ? "Parent leave (" + className + ")" : "Parent leave",
          });
          rangeStart = filteredDates[i].dateObj;
          rangeEnd = filteredDates[i].dateObj;
        }
      }

      entries.push({
        startDate: rangeStart,
        endDate: rangeEnd,
        addedOn: rangeStart,
        message: className !== "NA" ? "Parent leave (" + className + ")" : "Parent leave",
      });
    });

    return entries.sort(
      (a, b) => new Date(b.endDate || b.startDate) - new Date(a.endDate || a.startDate)
    );
  }, [classSessions]);

  const { columns: prCols3, rows: prRows3 } = ActivityAttendanceTableData4(leaveEntries);

  const [open, setOpen] = useState(false);
  const [leaveStartDate, setLeaveStartDate] = useState("");
  const [leaveEndDate, setLeaveEndDate] = useState("");
  const [leaveReason, setLeaveReason] = useState("");
  const [leaveSessionId, setLeaveSessionId] = useState("");
  const [submittingLeave, setSubmittingLeave] = useState(false);

  const handleClickOpen = () => {
    setLeaveStartDate("");
    setLeaveEndDate("");
    setLeaveReason("");
    const firstRegularSession = (Array.isArray(classSessions) ? classSessions : []).find(
      (session) => session?.type === "NORMAL" || session?.sessionType === "REGULAR_CLASS"
    );
    setLeaveSessionId(firstRegularSession?._id || "");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const buildLeaveDatesRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];

    const result = [];
    const cursor = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()));
    const endUtc = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()));

    while (cursor <= endUtc) {
      result.push(new Date(cursor));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }

    return result;
  };

  const handleSubmitLeave = async () => {
    if (!leaveSessionId) {
      toast.error("Please select a class session");
      return;
    }

    if (!leaveStartDate || !leaveEndDate) {
      toast.error("Please select both start and end date");
      return;
    }

    if (new Date(leaveEndDate) < new Date(leaveStartDate)) {
      toast.error("End date cannot be before start date");
      return;
    }

    const dates = buildLeaveDatesRange(leaveStartDate, leaveEndDate);
    if (dates.length === 0) {
      toast.error("Please select a valid date range");
      return;
    }

    try {
      setSubmittingLeave(true);
      const res = await axios.post("/attendance/mark-leave", {
        classSessionId: leaveSessionId,
        dates,
        leaveFor: "PARENT",
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Leave marked successfully");
        setOpen(false);
        setLeaveStartDate("");
        setLeaveEndDate("");
        setLeaveReason("");

        try {
          const classRes = await axios.get(
            `/classes/parent/${parentId}/accept-tutor?status=ACTIVE&limit=100`
          );
          if (classRes.data?.success) {
            setClassSessions(classRes.data.data || []);
          }
        } catch (refreshError) {
          console.error("Error refreshing class sessions:", refreshError);
        }
      } else {
        toast.error(res.data?.message || "Failed to mark leave");
      }
    } catch (err) {
      console.error("Error marking leave:", err);
      const apiMessage = err.response?.data?.message || err.response?.data?.msg;
      toast.error(apiMessage || "Failed to mark leave");
    } finally {
      setSubmittingLeave(false);
    }
  };

  const handleOpenNoteDialog = () => {
    setNoteInput("");
    setNoteDialogOpen(true);
  };

  const handleCloseNoteDialog = () => {
    if (savingNote) return;
    setNoteDialogOpen(false);
  };

  const handleOpenShowAllNotes = () => {
    setShowAllNotesOpen(true);
  };

  const handleCloseShowAllNotes = () => {
    setShowAllNotesOpen(false);
  };

  const handleSaveNote = async (e) => {
    if (e) e.preventDefault();
    if (!parentId) {
      toast.error("Parent ID is missing");
      return;
    }

    if (!noteInput.trim()) {
      toast.error("Please enter a note");
      return;
    }

    try {
      setSavingNote(true);
      const res = await axios.post(`/parent/${parentId}/notes`, {
        note: noteInput.trim(),
      });

      if (res.data?.success) {
        toast.success("Note added successfully");
        setNoteDialogOpen(false);
        setNoteInput("");
        const refreshed = await axios.get(`/parent/${parentId}/notes`);
        if (refreshed.data?.success) {
          setParentNotes(Array.isArray(refreshed.data.data) ? refreshed.data.data : []);
        }
      } else {
        throw new Error(res.data?.message || "Failed to add note");
      }
    } catch (err) {
      console.error("Error adding note:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  const minutesToHhMm = (minutes) => {
    if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return "NA";
    const totalMinutes = Math.max(0, Math.round(minutes));
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hours}:${mins.toString().padStart(2, "0")}`;
  };

  const formatDate = (value) => {
    if (!value) return "NA";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "NA";
    return date.toLocaleDateString("en-GB");
  };

  const formatTime = (value) => {
    if (!value) return "NA";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "NA";
    return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const safeTimeLabel = (value) => {
    const label = formatTime(value);
    return label === "NA" ? "" : label;
  };

  const getSessionLabel = (session) => {
    if (!session) return "Class Session";
    const studentName =
      session?.student?.name || session?.students?.[0]?.name || session?.student?.fullName;
    const className =
      session?.className ||
      session?.student?.class ||
      session?.students?.[0]?.class ||
      session?.name;
    const subjects = Array.isArray(session?.student?.subjects)
      ? session.student.subjects
      : Array.isArray(session?.students?.[0]?.subjects)
      ? session.students[0].subjects
      : [];
    const subjectLabel = subjects.length > 0 ? subjects.join(", ") : "";
    const slotStart = safeTimeLabel(session?.finalisedActiveSlot?.startTime);
    const slotEnd = safeTimeLabel(session?.finalisedActiveSlot?.endTime);
    const slotLabel = slotStart && slotEnd ? `${slotStart} - ${slotEnd}` : "";

    return [studentName, className || subjectLabel, slotLabel].filter(Boolean).join(" • ");
  };

  const leaveSessionOptions = useMemo(() => {
    return (Array.isArray(classSessions) ? classSessions : [])
      .filter((session) => session?.type === "NORMAL" || session?.sessionType === "REGULAR_CLASS")
      .filter((session) => session?._id)
      .map((session) => ({
        label: getSessionLabel(session),
        value: session._id,
      }));
  }, [classSessions]);

  const getAttendanceDuration = (attendance) => {
    if (attendance?.duration !== undefined && attendance?.duration !== null) {
      return minutesToHhMm(attendance.duration);
    }
    if (attendance?.startTime && attendance?.endTime) {
      const diffMinutes = (new Date(attendance.endTime) - new Date(attendance.startTime)) / 60000;
      return minutesToHhMm(diffMinutes);
    }
    return "NA";
  };

  const getAttendanceMarking = (attendance) => {
    const types = (Array.isArray(attendance?.type) ? attendance.type : [attendance?.type])
      .filter(Boolean)
      .map((type) => String(type).trim().toUpperCase());
    const hasEarlyAttendanceNote =
      typeof attendance?.earlyAttendanceNote === "string" &&
      attendance.earlyAttendanceNote.trim() !== "";

    if (attendance?.leaveFor) return "LEAVE";
    if (attendance?.earlyAttendance === true || hasEarlyAttendanceNote) return "EARLY CHECKOUT";
    if (types.includes("ABSENT")) return "ABSENT";
    return "CONDUCTED";
  };

  const getClassSessionStudentIds = (session) => {
    const sessionStudents = Array.isArray(session?.students)
      ? session.students
      : session?.student
      ? [session.student]
      : [];

    return sessionStudents
      .map((student) => (typeof student === "string" ? student : student?._id || student?.id))
      .filter(Boolean);
  };

  const getCycleLabel = (cycle, index) => {
    if (typeof cycle === "string" && cycle.trim()) return cycle.trim();

    return (
      cycle?.name ||
      cycle?.cycleName ||
      (cycle?.cycleNumber ? `Cycle ${cycle.cycleNumber}` : `Cycle ${index + 1}`)
    );
  };

  const getSubjectLabel = (attendance) => {
    const subjects = attendance?.classSessionId?.subjects || [];
    const subjectNames = subjects
      .map((subj) => subj?.name || subj?.subjectName)
      .filter(Boolean)
      .join(", ");
    return attendance?.topicCovered || subjectNames || "NA";
  };

  const getAttendanceStudentId = (attendance) => {
    if (!attendance) return null;
    if (attendance.__studentId) return attendance.__studentId;
    if (typeof attendance.studentId === "string") return attendance.studentId;
    if (attendance.studentId?._id) return attendance.studentId._id;
    const firstSessionStudent = attendance?.classSession?.students?.[0];
    if (typeof firstSessionStudent === "string") return firstSessionStudent;
    if (firstSessionStudent?._id) return firstSessionStudent._id;
    return null;
  };

  const scopedAttendanceForReport = useMemo(() => {
    if (reportScope.type === "STUDENT" && reportScope.studentId) {
      return (Array.isArray(attendanceData) ? attendanceData : []).filter(
        (attendance) => getAttendanceStudentId(attendance) === reportScope.studentId
      );
    }

    return Array.isArray(attendanceData) ? attendanceData : [];
  }, [attendanceData, reportScope]);

  const scopedStudentsForReport = useMemo(() => {
    if (reportScope.type === "STUDENT" && reportScope.studentId) {
      return (Array.isArray(students) ? students : []).filter(
        (student) => student?._id === reportScope.studentId
      );
    }

    return Array.isArray(students) ? students : [];
  }, [students, reportScope]);

  const scopedClassSessionsForReport = useMemo(() => {
    const sessions = Array.isArray(classSessions) ? classSessions : [];
    if (reportScope.type !== "STUDENT" || !reportScope.studentId) return sessions;

    return sessions.filter((session) =>
      getClassSessionStudentIds(session).includes(reportScope.studentId)
    );
  }, [classSessions, reportScope]);

  const buildReportData = (sourceAttendance, sourceStudents, sourceClassSessions) => {
    const list = Array.isArray(sourceAttendance) ? sourceAttendance : [];
    const sorted = [...list].sort((a, b) => {
      const aTime = new Date(a.attendanceTime || a.startTime || 0).getTime();
      const bTime = new Date(b.attendanceTime || b.startTime || 0).getTime();
      return aTime - bTime;
    });

    const scopedStudents =
      Array.isArray(sourceStudents) && sourceStudents.length > 0 ? sourceStudents : students;

    const studentNames = scopedStudents
      .map((student) => student?.name || student?.fullName)
      .filter(Boolean);
    const studentNameLabel =
      studentNames.length > 1
        ? `${studentNames[0]} +${studentNames.length - 1}`
        : studentNames[0] || "NA";

    const classNames = scopedStudents
      .map((student) => student?.class?.name || student?.class?.className)
      .filter(Boolean);
    const classNameLabel =
      classNames.length > 1 ? `${classNames[0]} +${classNames.length - 1}` : classNames[0] || "NA";

    const parentFromStudents =
      scopedStudents[0]?.parentId?.fullName || scopedStudents[0]?.parentId?.name || null;
    const parentName =
      parentFromStudents ||
      sorted[0]?.classSession?.students?.[0]?.parentId?.fullName ||
      sorted[0]?.classSession?.students?.[0]?.parentId?.name ||
      sorted[0]?.classSessionId?.parentId?.fullName ||
      sorted[0]?.classSessionId?.parentId?.name ||
      "NA";
    const tutorName =
      sorted[0]?.classSession?.tutors?.[0]?.name ||
      sorted[0]?.classSession?.tutors?.[0]?.fullName ||
      sorted[0]?.classSessionId?.tutorId?.fullName ||
      sorted[0]?.tutor?.name ||
      sorted[0]?.tutor?.fullName ||
      "NA";
    const className =
      classNameLabel ||
      sorted[0]?.classSession?.className ||
      sorted[0]?.classSessionId?.className ||
      sorted[0]?.classSessionId?.name ||
      "NA";
    const boardName =
      scopedStudents[0]?.board?.name ||
      scopedStudents[0]?.board?.boardName ||
      sorted[0]?.classSessionId?.board?.name ||
      "NA";

    const uniqueSessions = new Map();
    sorted.forEach((item) => {
      const session = item?.classSession || item?.classSessionId;
      if (session?.id && !uniqueSessions.has(session.id)) {
        uniqueSessions.set(session.id, session);
      }
      if (session?._id && !uniqueSessions.has(session._id)) {
        uniqueSessions.set(session._id, session);
      }
    });
    const progressTotal = Number(sorted[0]?.classProgress?.totalClasses || 0);
    const sessionsAssigned = progressTotal
      ? progressTotal
      : Array.from(uniqueSessions.values()).reduce((sum, session) => {
          const total = Number(session?.totalNoOfClasses || 0);
          return sum + total;
        }, 0);

    const totalMinutes = sorted.reduce((sum, attendance) => {
      if (attendance?.duration !== undefined && attendance?.duration !== null) {
        return sum + Number(attendance.duration || 0);
      }
      if (attendance?.startTime && attendance?.endTime) {
        return sum + (new Date(attendance.endTime) - new Date(attendance.startTime)) / 60000;
      }
      return sum;
    }, 0);

    const firstDate = sorted[0]?.attendanceTime || sorted[0]?.startTime;
    const lastDate =
      sorted[sorted.length - 1]?.attendanceTime || sorted[sorted.length - 1]?.startTime;

    const uniqueCycles = new Map();
    const addCycle = (cycleLabel) => {
      const label = String(cycleLabel || "").trim();
      if (!label) return;

      const key = label.toLowerCase();
      if (!uniqueCycles.has(key)) uniqueCycles.set(key, label);
    };

    (Array.isArray(sourceClassSessions) ? sourceClassSessions : []).forEach((session) => {
      const cycles = Array.isArray(session?.cycles) ? session.cycles : [];
      cycles.forEach((cycle, index) => addCycle(getCycleLabel(cycle, index)));
    });

    let totalConductedCount = 0;
    const attendanceMarkings = new Set();
    sorted.forEach((item) => {
      const cycle =
        item?.attendance?.currentCycle ||
        item?.currentCycle ||
        item?.cycleName ||
        item?.classSession?.currentCycle ||
        item?.classSessionId?.currentCycle;
      addCycle(cycle);

      const marking = getAttendanceMarking(item);
      attendanceMarkings.add(marking);
      if (marking === "CONDUCTED") {
        totalConductedCount++;
      }
    });

    const cycleNames = Array.from(uniqueCycles.values()).join(", ");
    const totalCycles = uniqueCycles.size;
    const attendanceType =
      attendanceMarkings.size === 1
        ? Array.from(attendanceMarkings)[0]
        : attendanceMarkings.size > 1
        ? "MIXED"
        : "NA";

    return {
      list: sorted,
      summary: {
        totalClasses: sorted.length,
        totalHours: minutesToHhMm(totalMinutes),
        tutorName,
        studentName: studentNameLabel,
        parentName,
        standard: className,
        board: boardName,
        session: "2024-25",
        sessionsAssigned: sessionsAssigned > 0 ? sessionsAssigned : "NA",
        classStartingDate: formatDate(firstDate),
        lastMarkedDate: formatDate(lastDate),
        attendanceType,
        cycleNames: cycleNames || "NA",
        totalCycles: totalCycles || "0",
        totalConductedCount,
      },
    };
  };

  const handleDownloadReport = async (scope = { type: "ALL", studentId: null }) => {
    if (!reportRef.current) return;
    if (downloadingReport) return;

    setReportScope(scope);

    // Wait for hidden report DOM to update before capture.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    setDownloadingReport(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");

      // Calculate dimensions to fit exactly on a single continuous PDF page
      const orientation = canvas.width > canvas.height ? "landscape" : "portrait";
      const pdf = new jsPDF(orientation, "pt", [canvas.width, canvas.height]);

      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);

      const blob = pdf.output("blob");
      const url = window.URL.createObjectURL(blob);

      // Open PDF in a new tab for preview; browser provides optional download action.
      const newWindow = window.open(url, "_blank");

      if (newWindow) {
        newWindow.onload = () => {
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 5000);
        };
      } else {
        toast.info("Popup blocked. Please allow popups to preview the PDF.");
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 10000);
      }
    } catch (downloadError) {
      console.error("Error generating report:", downloadError);
      toast.error("Failed to download report. Please try again.");
    } finally {
      setDownloadingReport(false);
      if (scope.type !== "ALL") {
        setReportScope({ type: "ALL", studentId: null });
      }
    }
  };

  const reportData = buildReportData(
    scopedAttendanceForReport,
    scopedStudentsForReport,
    scopedClassSessionsForReport
  );

  return (
    <ArgonBox>
      {/* <ArgonBox display="flex"px={2} py={1} mt={2} justifyContent="space-between" alignItems="center">
        <ArgonTypography variant="body1" fontWeight="medium">Class</ArgonTypography>
        <ArgonBox display="flex" flexDirection="column" gap={1}>
            <ArgonTypography variant="caption" fontWeight="medium">Total hours taught: 44 hours</ArgonTypography>
            <ArgonTypography variant="caption" fontWeight="medium">Total hours taught: 32 hours</ArgonTypography>
        </ArgonBox>
     </ArgonBox> */}

      {loading ? (
        <ArgonBox>
          <Card sx={{ p: 5 }}>
            <ArgonBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={2}
            >
              <ArgonTypography variant="h6" color="text">
                Loading Student List...
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </ArgonBox>
      ) : error ? (
        <ArgonBox>
          <Card sx={{ p: 5 }}>
            <ArgonBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={2}
            >
              <EventNoteIcon sx={{ fontSize: 64, color: "#d0d0d0" }} />
              <ArgonTypography variant="h6" color="error">
                {error}
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </ArgonBox>
      ) : rows.length === 0 ? (
        <ArgonBox>
          <Card sx={{ p: 5 }}>
            <ArgonBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={2}
            >
              <EventNoteIcon sx={{ fontSize: 64, color: "#d0d0d0" }} />
              <ArgonTypography variant="h6" color="text">
                No Students Available
              </ArgonTypography>
              <ArgonTypography variant="caption" color="text" textAlign="center">
                This parent has no students enrolled yet.
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </ArgonBox>
      ) : (
        <ArgonBox py={3}>
          <ArgonTypography variant="body1" fontWeight="medium" sx={{ px: 3 }} mb={2}>
            Student List
          </ArgonTypography>
          <ArgonBox
            sx={{
              "& .MuiTableRow-root:not(:last-child)": {
                "& td": {
                  borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                    `${borderWidth[1]} solid ${borderColor}`,
                },
              },
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            {Array.isArray(rows) && <Table columns={columns} rows={rows} />}
          </ArgonBox>
        </ArgonBox>
      )}

      <Grid container spacing={3} mt={5}>
        <Grid item xs={12} lg={6}>
          <ArgonBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2, py: 1.2, bgcolor: "#f8f9fa", borderRadius: 2, mb: 2 }}
          >
            <ArgonTypography variant="h6" fontWeight="bold" color="primary">
              Attendance
            </ArgonTypography>
            <ArgonBox display="flex" gap={2}>
              {/* <ArgonButton
                sx={{ fontSize: "0.675rem", px: "18px", py: "1px" }}
                color="primary"
                variant="contained"
                size="small"
              >
                Add Record
              </ArgonButton> */}
              <ArgonButton
                variant="outlined"
                size="small"
                color="primary"
                onClick={() => handleDownloadReport({ type: "ALL", studentId: null })}
                disabled={downloadingReport || reportData.list.length === 0}
              >
                {downloadingReport ? "Preparing..." : "Download Report"}
              </ArgonButton>
            </ArgonBox>
          </ArgonBox>

          {loading ? (
            <ArgonBox>
              <Card sx={{ p: 5 }}>
                <ArgonBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap={2}
                >
                  <ArgonTypography variant="h6" color="text">
                    Loading Attendance Data...
                  </ArgonTypography>
                </ArgonBox>
              </Card>
            </ArgonBox>
          ) : error ? (
            <ArgonBox py={3}>
              <ArgonTypography variant="body1" fontWeight="medium" sx={{ px: 3 }} color="error">
                {error}
              </ArgonTypography>
            </ArgonBox>
          ) : prRows.length === 0 ? (
            <ArgonBox>
              <Card sx={{ p: 5 }}>
                <ArgonBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap={2}
                >
                  <EventNoteIcon sx={{ fontSize: 64, color: "#d0d0d0" }} />
                  <ArgonTypography variant="h6" color="text">
                    No Attendance Data Available
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="text" textAlign="center">
                    No attendance records found for this parent&apos;s students.
                  </ArgonTypography>
                </ArgonBox>
              </Card>
            </ArgonBox>
          ) : (
            <ArgonBox py={3}>
              <ArgonBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                  maxHeight: "400px",
                  overflowY: "auto",
                }}
              >
                {Array.isArray(prRows) && <Table columns={prCols} rows={prRows} />}
              </ArgonBox>
            </ArgonBox>
          )}
        </Grid>

        <Grid item xs={12} lg={6}>
          <ArgonBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2, py: 1.2, bgcolor: "#f8f9fa", borderRadius: 2, mb: 2 }}
          >
            <ArgonTypography variant="h6" fontWeight="bold" color="primary">
              Support & Notes
            </ArgonTypography>
            <ArgonButton
              type="button"
              variant="contained"
              size="small"
              color="primary"
              onClick={handleOpenNoteDialog}
              disabled={notesLoading}
            >
              Add Note
            </ArgonButton>
          </ArgonBox>

          <ArgonBox py={3}>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <Table columns={prCols2} rows={prRows2} />
            </ArgonBox>
            <ArgonBox display="flex" justifyContent="flex-end" mt={1.5}>
              <ArgonTypography
                variant="caption"
                color="primary"
                sx={{ cursor: "pointer", fontWeight: "bold" }}
                onClick={handleOpenShowAllNotes}
              >
                Show All Notes →
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>

          <ArgonBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2, py: 1.1, bgcolor: "#f8f9fa", borderRadius: 2, mb: 2 }}
          >
            <ArgonTypography variant="h6" fontWeight="bold" color="primary">
              Leave & Holiday
            </ArgonTypography>
            <ArgonButton onClick={handleClickOpen} variant="contained" size="small" color="primary">
              Add
            </ArgonButton>
          </ArgonBox>

          <ArgonBox py={3}>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <Table columns={prCols3} rows={prRows3} />
            </ArgonBox>
          </ArgonBox>
        </Grid>
      </Grid>

      <Dialog
        open={noteDialogOpen}
        onClose={handleCloseNoteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Parent Note</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <ArgonInput
              multiline
              rows={4}
              fullWidth
              placeholder="Enter note"
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <ArgonButton variant="outlined" color="secondary" onClick={handleCloseNoteDialog}>
            Cancel
          </ArgonButton>
          <ArgonButton
            type="button"
            variant="contained"
            color="primary"
            onClick={handleSaveNote}
            disabled={savingNote}
          >
            {savingNote ? "Saving..." : "Save"}
          </ArgonButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showAllNotesOpen}
        onClose={handleCloseShowAllNotes}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>All Notes</DialogTitle>
        <DialogContent>
          <ArgonBox py={2}>
            {notesLoading ? (
              <ArgonBox display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} />
              </ArgonBox>
            ) : prRows2.length === 0 ? (
              <ArgonBox display="flex" flexDirection="column" alignItems="center" py={4}>
                <EventNoteIcon sx={{ fontSize: 48, color: "#d0d0d0" }} />
                <ArgonTypography variant="body2" color="text" mt={1}>
                  No notes available
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <ArgonBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                <Table columns={prCols2} rows={prRows2} />
              </ArgonBox>
            )}
          </ArgonBox>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <ArgonButton variant="outlined" color="secondary" onClick={handleCloseShowAllNotes}>
            Close
          </ArgonButton>
        </DialogActions>
      </Dialog>

      {/* Enhanced Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            textAlign: "center",
            py: 2,
          }}
        >
          <ArgonBox display="flex" alignItems="center" justifyContent="center" gap={1}>
            <ArgonTypography variant="h5" fontWeight="bold" color="white">
              Add Leave/Holiday Request
            </ArgonTypography>
          </ArgonBox>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <ArgonBox mb={3}>
            <ArgonTypography variant="body2" color="text" mb={2} textAlign="center">
              Please fill in the details for your leave or holiday request
            </ArgonTypography>
          </ArgonBox>

          <ArgonBox mb={3}>
            <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                Select Class Session
              </ArgonTypography>
            </ArgonBox>
            <Autocomplete
              size="small"
              options={leaveSessionOptions}
              value={leaveSessionOptions.find((option) => option.value === leaveSessionId) || null}
              onChange={(event, option) => setLeaveSessionId(option?.value || "")}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select class" fullWidth />
              )}
            />
          </ArgonBox>

          <ArgonBox mb={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                  <ArgonTypography variant="caption" fontWeight="medium" color="text">
                    Start Date
                  </ArgonTypography>
                </ArgonBox>
                <ArgonInput
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={leaveStartDate}
                  onChange={(e) => setLeaveStartDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                  <ArgonTypography variant="caption" fontWeight="medium" color="text">
                    End Date
                  </ArgonTypography>
                </ArgonBox>
                <ArgonInput
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={leaveEndDate}
                  onChange={(e) => setLeaveEndDate(e.target.value)}
                  inputProps={leaveStartDate ? { min: leaveStartDate } : undefined}
                />
              </Grid>
            </Grid>
          </ArgonBox>

          <ArgonBox mb={0}>
            <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                Reason/Message
              </ArgonTypography>
            </ArgonBox>
            <ArgonInput
              placeholder="Please describe the reason for your leave request..."
              multiline
              rows={4}
              fullWidth
              size="small"
              value={leaveReason}
              onChange={(e) => setLeaveReason(e.target.value)}
            />
          </ArgonBox>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2, justifyContent: "center" }}>
          <ArgonButton onClick={handleClose} variant="outlined" color="primary">
            Cancel
          </ArgonButton>
          <ArgonButton
            variant="contained"
            onClick={handleSubmitLeave}
            color="primary"
            disabled={submittingLeave}
          >
            {submittingLeave ? "Submitting..." : "Submit Request"}
          </ArgonButton>
        </DialogActions>
      </Dialog>

      {/* Student Edit Dialog */}
      {studentEditOpen && selectedStudent && (
        <ArgonBox
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(5px)",
          }}
        >
          <ArgonBox
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "20px",
              width: "90%",
              maxWidth: 600,
              maxHeight: "95vh",
              overflowY: "auto",
              boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
              position: "relative",
            }}
          >
            {/* Header */}
            <ArgonBox
              sx={{
                background: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(10px)",
                borderRadius: "20px 20px 0 0",
                p: 3,
                borderBottom: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                <ArgonBox display="flex" alignItems="center" gap={2}>
                  <ArgonBox
                    sx={{
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "12px",
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <EditIcon sx={{ color: "white", fontSize: "24px" }} />
                  </ArgonBox>
                  <ArgonBox>
                    <ArgonTypography variant="h5" fontWeight="medium" color="white">
                      Edit Student Details
                    </ArgonTypography>
                    <ArgonTypography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                      Update student information
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>
                <ArgonButton
                  variant="text"
                  onClick={handleStudentEditClose}
                  sx={{
                    color: "white",
                    minWidth: "40px",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.1)",
                    "&:hover": { background: "rgba(255,255,255,0.2)" },
                  }}
                >
                  ×
                </ArgonButton>
              </ArgonBox>
            </ArgonBox>

            {/* Form Content */}
            <ArgonBox sx={{ background: "white", borderRadius: "0 0 20px 20px", p: 4 }}>
              <form onSubmit={handleUpdateStudent}>
                <Grid container spacing={3}>
                  {/* Student Name */}
                  <Grid item xs={12}>
                    <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                      Student Name
                    </ArgonTypography>
                    <ArgonInput
                      placeholder="Enter Student Name"
                      value={studentName}
                      onChange={(e) => setStudentName(e.target.value)}
                      fullWidth
                      size="medium"
                    />
                  </Grid>

                  {/* Gender */}
                  <Grid item xs={12} sm={6}>
                    <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                      Gender *
                    </ArgonTypography>
                    <Dropdown
                      value={studentGender}
                      options={[
                        { label: "Select Gender", value: "" },
                        { label: "Male", value: "MALE" },
                        { label: "Female", value: "FEMALE" },
                        { label: "Other", value: "OTHER" },
                      ]}
                      onChange={(e) => setStudentGender(e.target.value)}
                      width="100%"
                      size="medium"
                    />
                  </Grid>

                  {/* Date of Birth */}
                  <Grid item xs={12} sm={6}>
                    <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                      Date of Birth
                    </ArgonTypography>
                    <ArgonInput
                      type="date"
                      value={studentDob}
                      onChange={(e) => setStudentDob(e.target.value)}
                      fullWidth
                      size="medium"
                    />
                  </Grid>

                  {/* School Name */}
                  <Grid item xs={12}>
                    <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                      School/College/University Name
                    </ArgonTypography>
                    <ArgonInput
                      placeholder="Enter School Name"
                      value={studentSchoolName}
                      onChange={(e) => setStudentSchoolName(e.target.value)}
                      fullWidth
                      size="medium"
                    />
                  </Grid>

                  {/* Class */}
                  <Grid item xs={12} sm={6}>
                    <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                      Class
                    </ArgonTypography>
                    <Dropdown
                      value={studentClass}
                      options={[
                        { label: "Select Class", value: "" },
                        ...allClasses.map((cls) => ({ label: cls.label, value: cls.value })),
                      ]}
                      onChange={(e) => setStudentClass(e.target.value)}
                      width="100%"
                      size="medium"
                    />
                  </Grid>

                  {/* Board */}
                  <Grid item xs={12} sm={6}>
                    <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                      Board
                    </ArgonTypography>
                    <Dropdown
                      value={studentBoard}
                      options={[
                        { label: "Select Board", value: "" },
                        ...allBoards.map((board) => ({ label: board.label, value: board.value })),
                      ]}
                      onChange={(e) => setStudentBoard(e.target.value)}
                      width="100%"
                      size="medium"
                    />
                  </Grid>

                  {/* Subjects (Multi-select) */}
                  <Grid item xs={12}>
                    <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                      Subjects
                    </ArgonTypography>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={allSubjects}
                      value={allSubjects.filter((subject) =>
                        studentSubjects.includes(subject.value)
                      )}
                      onChange={(event, newValue) => {
                        setStudentSubjects(newValue.map((subject) => subject.value));
                      }}
                      getOptionLabel={(option) => option.label || ""}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select subjects"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "45px",
                              border: "1px solid #d2d6da",
                              borderRadius: "8px",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.value}
                            label={option.label}
                            deleteIcon={<CancelIcon />}
                            sx={{
                              backgroundColor: "#667eea",
                              color: "white",
                              "& .MuiChip-deleteIcon": {
                                color: "white",
                                "&:hover": {
                                  color: "rgba(255,255,255,0.7)",
                                },
                              },
                            }}
                          />
                        ))
                      }
                    />
                  </Grid>

                  {/* Preferred Time Duration */}
                  <Grid item xs={12} sm={6}>
                    <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                      Preferred Time Duration (mins)
                    </ArgonTypography>
                    <ArgonInput
                      placeholder="Enter duration in minutes"
                      value={studentPreferredTime}
                      onChange={(e) => setStudentPreferredTime(e.target.value)}
                      fullWidth
                      size="medium"
                      type="number"
                    />
                  </Grid>

                  {/* Classes Per Week */}
                  <Grid item xs={12} sm={6}>
                    <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                      Classes Per Week
                    </ArgonTypography>
                    <ArgonInput
                      placeholder="Enter classes per week"
                      value={studentClassesPerWeek}
                      onChange={(e) => setStudentClassesPerWeek(e.target.value)}
                      fullWidth
                      size="medium"
                      type="number"
                    />
                  </Grid>

                  {/* Mode of Tuition */}
                  <Grid item xs={12}>
                    <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                      Mode of Tuition *
                    </ArgonTypography>
                    <Dropdown
                      value={studentModeOfTuition}
                      options={[
                        { label: "Select Mode of Tuition", value: "" },
                        { label: "Online", value: "ONLINE" },
                        { label: "Offline", value: "OFFLINE" },
                        { label: "Both", value: "BOTH" },
                      ]}
                      onChange={(e) => setStudentModeOfTuition(e.target.value)}
                      width="100%"
                      size="medium"
                    />
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12}>
                    <ArgonBox display="flex" justifyContent="flex-end" gap={2} mt={2}>
                      <ArgonButton
                        variant="outlined"
                        color="secondary"
                        onClick={handleStudentEditClose}
                        disabled={updatingStudent}
                      >
                        Cancel
                      </ArgonButton>
                      <ArgonButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={updatingStudent}
                        startIcon={
                          updatingStudent && <CircularProgress size={16} color="inherit" />
                        }
                      >
                        {updatingStudent ? "Updating..." : "Update Student"}
                      </ArgonButton>
                    </ArgonBox>
                  </Grid>
                </Grid>
              </form>
            </ArgonBox>
          </ArgonBox>
        </ArgonBox>
      )}

      {/* Delete Student Confirmation Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={handleStudentDeleteClose}
        onConfirm={handleStudentDeleteConfirm}
        title="Delete Student"
        description={`Are you sure you want to delete ${
          studentToDelete?.name || "this student"
        }? This action cannot be undone.`}
        confirmText="Delete Student"
        cancelText="Cancel"
      />

      <div
        ref={reportRef}
        style={{
          position: "fixed",
          left: "-10000px",
          top: 0,
          width: "1120px",
          background: "#ffffff",
          color: "#0b2b5b",
          fontFamily: "Arial, sans-serif",
          padding: "16px",
          border: "1px solid #0b2b5b",
        }}
      >
        <div style={{ border: "2px solid #0b2b5b", borderRadius: "8px", overflow: "hidden" }}>
          {/* Header section similar to medical records */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "20px 30px",
              borderBottom: "2px solid #0b2b5b",
            }}
          >
            <div style={{ flex: "0 0 120px" }}>
              <img
                src={logoEdushala}
                alt="EDUSHALA"
                style={{ height: "80px", objectFit: "contain" }}
              />
            </div>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: "28px",
                  color: "#0b2b5b",
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                }}
              >
                EDUSHALA
              </div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "16px",
                  color: "#2d6cdf",
                  marginTop: "8px",
                  letterSpacing: "1px",
                }}
              >
                EDUSHALA ATTENDANCE MONITORING
              </div>
            </div>
            <div style={{ flex: "0 0 120px", textAlign: "right" }}>
              <div style={{ fontWeight: 700, fontSize: "12px", color: "#666" }}>
                Report Generated:
              </div>
              <div style={{ fontWeight: 800, fontSize: "14px", color: "#0b2b5b" }}>
                {new Date().toLocaleDateString("en-GB")}
              </div>
            </div>
          </div>

          {/* Details Grid Section */}
          <div
            style={{ display: "flex", borderBottom: "2px solid #0b2b5b", background: "#f8faff" }}
          >
            <div style={{ flex: 1, borderRight: "1px solid #0b2b5b", padding: "16px" }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}
              >
                <PersonIcon sx={{ color: "#2d6cdf", fontSize: "20px" }} />
                <span style={{ fontWeight: 700, fontSize: "14px", color: "#0b2b5b" }}>
                  STUDENT DETAILS
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "100px 1fr",
                  gap: "8px",
                  fontSize: "13px",
                }}
              >
                <div style={{ fontWeight: 700, color: "#666" }}>NAME:</div>
                <div style={{ fontWeight: 800, color: "#0b2b5b" }}>
                  {reportData.summary.studentName}
                </div>
                <div style={{ fontWeight: 700, color: "#666" }}>PARENT/G.:</div>
                <div style={{ fontWeight: 800, color: "#0b2b5b" }}>
                  {reportData.summary.parentName}
                </div>
                <div style={{ fontWeight: 700, color: "#666" }}>TUTOR:</div>
                <div style={{ fontWeight: 800, color: "#0b2b5b" }}>
                  {reportData.summary.tutorName}
                </div>
              </div>
            </div>

            <div style={{ flex: 1, borderRight: "1px solid #0b2b5b", padding: "16px" }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}
              >
                <MenuBookIcon sx={{ color: "#2d6cdf", fontSize: "20px" }} />
                <span style={{ fontWeight: 700, fontSize: "14px", color: "#0b2b5b" }}>
                  ACADEMIC DETAILS
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "90px 1fr",
                  gap: "8px",
                  fontSize: "13px",
                }}
              >
                <div style={{ fontWeight: 700, color: "#666" }}>STANDARD:</div>
                <div style={{ fontWeight: 800, color: "#0b2b5b" }}>
                  {reportData.summary.standard}
                </div>
                <div style={{ fontWeight: 700, color: "#666" }}>BOARD:</div>
                <div style={{ fontWeight: 800, color: "#0b2b5b" }}>{reportData.summary.board}</div>
                <div style={{ fontWeight: 700, color: "#666" }}>SESSION:</div>
                <div style={{ fontWeight: 800, color: "#0b2b5b" }}>
                  {reportData.summary.session}
                </div>
              </div>
            </div>

            <div style={{ flex: 1, padding: "16px" }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}
              >
                <AssessmentIcon sx={{ color: "#2d6cdf", fontSize: "20px" }} />
                <span style={{ fontWeight: 700, fontSize: "14px", color: "#0b2b5b" }}>SUMMARY</span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "130px 1fr",
                  gap: "8px",
                  fontSize: "13px",
                }}
              >
                <div style={{ fontWeight: 700, color: "#666" }}>TOTAL ASSIGNED:</div>
                <div style={{ fontWeight: 800, color: "#0b2b5b" }}>
                  {reportData.summary.sessionsAssigned}
                </div>
                <div style={{ fontWeight: 700, color: "#666" }}>TOTAL CONDUCTED:</div>
                <div style={{ fontWeight: 800, color: "#0b2b5b" }}>
                  {reportData.summary.totalConductedCount}
                </div>
                <div style={{ fontWeight: 700, color: "#666" }}>TOTAL CYCLES:</div>
                <div style={{ fontWeight: 800, color: "#2d6cdf" }}>
                  {reportData.summary.cycleNames}
                </div>
                <div style={{ fontWeight: 700, color: "#666" }}>TOTAL DURATION:</div>
                <div style={{ fontWeight: 800, color: "#0b2b5b" }}>
                  {reportData.summary.totalHours} hrs
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              borderBottom: "2px solid #0b2b5b",
              background: "#ffffff",
              padding: "12px 16px",
            }}
          >
            <div style={{ flex: 1, display: "flex", gap: "16px", justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: "8px", fontSize: "13px", alignItems: "center" }}>
                <div style={{ fontWeight: 700, color: "#666" }}>CLASS STARTING DATE:</div>
                <div style={{ fontWeight: 800, color: "#0b2b5b" }}>
                  {reportData.summary.classStartingDate}
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", fontSize: "13px", alignItems: "center" }}>
                <div style={{ fontWeight: 700, color: "#666" }}>LAST MARKED DATE:</div>
                <div style={{ fontWeight: 800, color: "#0b2b5b" }}>
                  {reportData.summary.lastMarkedDate}
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", fontSize: "13px", alignItems: "center" }}>
                <div style={{ fontWeight: 700, color: "#666" }}>ATTENDANCE TYPE:</div>
                <div style={{ fontWeight: 800, color: "#0b2b5b" }}>
                  {reportData.summary.attendanceType}
                </div>
              </div>
            </div>
          </div>

          <div>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "12px",
              }}
            >
              <thead>
                <tr style={{ background: "#2d6cdf", color: "#ffffff" }}>
                  <th style={{ border: "1px solid #0b2b5b", padding: "10px", width: "5%" }}>S.N</th>
                  <th style={{ border: "1px solid #0b2b5b", padding: "10px", width: "10%" }}>
                    DATE
                  </th>
                  <th style={{ border: "1px solid #0b2b5b", padding: "10px", width: "10%" }}>
                    DAY
                  </th>
                  <th style={{ border: "1px solid #0b2b5b", padding: "10px", width: "10%" }}>
                    TIME IN
                  </th>
                  <th style={{ border: "1px solid #0b2b5b", padding: "10px", width: "10%" }}>
                    TIME OUT
                  </th>
                  <th style={{ border: "1px solid #0b2b5b", padding: "10px", width: "10%" }}>
                    DURATION
                  </th>
                  <th style={{ border: "1px solid #0b2b5b", padding: "10px" }}>SUBJECT</th>
                  <th style={{ border: "1px solid #0b2b5b", padding: "10px", width: "10%" }}>
                    LEAVE FOR
                  </th>
                  <th style={{ border: "1px solid #0b2b5b", padding: "10px", width: "12%" }}>
                    STATUS
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportData.list.map((attendance, index) => {
                  const dateValue = attendance.attendanceTime || attendance.startTime;
                  const date = dateValue ? new Date(dateValue) : null;
                  const dayName = date
                    ? date.toLocaleDateString("en-US", { weekday: "long" })
                    : "NA";
                  return (
                    <tr
                      key={attendance._id || index}
                      style={{ background: index % 2 === 0 ? "#ffffff" : "#f8faff" }}
                    >
                      <td
                        style={{
                          border: "1px solid #0b2b5b",
                          padding: "8px",
                          textAlign: "center",
                          fontWeight: 700,
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{ border: "1px solid #0b2b5b", padding: "8px", textAlign: "center" }}
                      >
                        {formatDate(dateValue)}
                      </td>
                      <td
                        style={{ border: "1px solid #0b2b5b", padding: "8px", textAlign: "center" }}
                      >
                        {dayName}
                      </td>
                      <td
                        style={{ border: "1px solid #0b2b5b", padding: "8px", textAlign: "center" }}
                      >
                        {formatTime(attendance.startTime || attendance.attendanceTime)}
                      </td>
                      <td
                        style={{ border: "1px solid #0b2b5b", padding: "8px", textAlign: "center" }}
                      >
                        {formatTime(attendance.endTime)}
                      </td>
                      <td
                        style={{
                          border: "1px solid #0b2b5b",
                          padding: "8px",
                          textAlign: "center",
                          fontWeight: 700,
                        }}
                      >
                        {getAttendanceDuration(attendance)}
                      </td>
                      <td style={{ border: "1px solid #0b2b5b", padding: "8px" }}>
                        {getSubjectLabel(attendance)}
                      </td>
                      <td
                        style={{
                          border: "1px solid #0b2b5b",
                          padding: "8px",
                          textAlign: "center",
                          fontWeight: "600",
                          color: attendance.leaveFor ? "#d32f2f" : "#666",
                        }}
                      >
                        {attendance.leaveFor || "NA"}
                      </td>
                      <td
                        style={{
                          border: "1px solid #0b2b5b",
                          padding: "8px",
                          color:
                            getAttendanceMarking(attendance) === "CONDUCTED"
                              ? "#2e7d32"
                              : "#d32f2f",
                          fontWeight: 800,
                          textAlign: "center",
                        }}
                      >
                        {getAttendanceMarking(attendance)}
                      </td>
                    </tr>
                  );
                })}
                {reportData.list.length === 0 && (
                  <tr>
                    <td
                      colSpan="9"
                      style={{
                        border: "1px solid #0b2b5b",
                        padding: "30px",
                        textAlign: "center",
                        fontWeight: 700,
                        color: "#666",
                      }}
                    >
                      No Attendance Data Available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "16px 24px",
                background: "#0b2b5b",
                fontWeight: 800,
                color: "#ffffff",
                fontSize: "16px",
                textTransform: "uppercase",
              }}
            >
              <span>RECORD END</span>
              <span>{reportData.list.length} SESSIONS LOGGED</span>
            </div>
          </div>
        </div>
      </div>
    </ArgonBox>
  );
}

activityAttendance.propTypes = {
  parentId: PropTypes.string.isRequired,
  onStatusChange: PropTypes.func,
};

export default activityAttendance;
